import { CustomColumnGridConfig } from '../types'
import type { ColumnFilterKind } from '../Filter/filterKinds'
import { collectValueNames, renderCodeCell } from '@/utils/codeName'

/** Fixed columns in display order – keyed by the JSON field name coming from the API */
const FIXED_COLUMNS: CustomColumnGridConfig[] = [
  {
    width: 150,
    minWidth: 120,
    flex: 1,
    field: 'billingId',
    headerName: 'כרטיס מים',
    filterSpec: { kind: 'text' },
    filterable: true,
    hideable: true,
    disableAggregation: true,
  },
  {
    width: 150,
    minWidth: 120,
    flex: 1,
    field: 'meterNumber',
    headerName: 'מספר מד',
    filterSpec: { kind: 'text' },
    filterable: true,
    hideable: true,
    disableAggregation: true,
  },
  {
    width: 150,
    minWidth: 120,
    flex: 1,
    field: 'meterModelName',
    headerName: 'דגם מונה',
    filterSpec: { kind: 'list' },
    filterable: true,
    hideable: true,
    disableAggregation: true,
  },
  {
    width: 150,
    minWidth: 120,
    flex: 1,
    field: 'longitude',
    headerName: 'קו אורך',
    filterSpec: { kind: 'none' },
    filterable: false,
    hideable: true,
    disableAggregation: true,
  },
  {
    width: 150,
    minWidth: 120,
    flex: 1,
    field: 'latitude',
    headerName: 'קו רוחב',
    filterSpec: { kind: 'none' },
    filterable: false,
    hideable: true,
    disableAggregation: true,
  },
  {
    width: 160,
    minWidth: 130,
    flex: 1,
    field: 'startReading',
    headerName: 'קריאה התחלתית',
    filterSpec: { kind: 'none' },
    filterable: false,
    hideable: true,
    disableAggregation: true,
  },
  {
    width: 180,
    minWidth: 150,
    flex: 1,
    field: 'startReadingAt',
    headerName: 'ת.קריאה התחלתית',
    filterSpec: { kind: 'none' },
    filterable: false,
    hideable: true,
    disableAggregation: true,
    renderCell: (params: any) => {
      if (!params.value) return '-'
      const d = new Date(params.value)
      return d.toLocaleDateString('he-IL') + ' ' + d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
    },
  },
  {
    width: 160,
    minWidth: 130,
    flex: 1,
    field: 'endReading',
    headerName: 'קריאה סופית',
    filterSpec: { kind: 'none' },
    filterable: false,
    hideable: true,
    disableAggregation: true,
  },
  {
    width: 180,
    minWidth: 150,
    flex: 1,
    field: 'endReadingAt',
    headerName: 'ת.קריאה סופית',
    filterSpec: { kind: 'none' },
    filterable: false,
    hideable: true,
    disableAggregation: true,
    renderCell: (params: any) => {
      if (!params.value) return '-'
      const d = new Date(params.value)
      return d.toLocaleDateString('he-IL') + ' ' + d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
    },
  },
  {
    width: 150,
    minWidth: 120,
    flex: 1,
    field: 'consumption',
    headerName: 'צריכה',
    filterSpec: { kind: 'none' },
    filterable: false,
    hideable: true,
    disableAggregation: true,
  },
]

type ReportResource = {
  key: string
  type?: string
  value?: unknown
  valueName?: string | null
}
type ReportReading = { resources?: ReportResource[]; [field: string]: unknown }

const resourcesOf = (row: ReportReading): ReportResource[] =>
  Array.isArray(row.resources) ? row.resources : []

/**
 * The Gateway names the data type of each resource. A code that has a name is searched as text,
 * because the Gateway then matches the code and the name. An unknown type is searched as text too.
 */
const resourceFilterKind = (type: string | undefined, hasName: boolean): ColumnFilterKind => {
  if (hasName) return 'text'
  if (type === 'int' || type === 'number') return 'number'
  if (type === 'timestamp' || type === 'date') return 'date'
  return 'text'
}

/**
 * Build columns dynamically from the API response rows.
 * Fixed columns come first, then any dynamic resource columns found in rows[].resources.
 */
export function buildSelectedMeterReadingsColumns(rows: any[]): CustomColumnGridConfig[] {
  // Collect fixed column fields and headerNames so we skip duplicates from resources
  const fixedFields = new Set(FIXED_COLUMNS.map(c => c.field))
  const fixedHeaders = new Set(FIXED_COLUMNS.map(c => c.headerName))

  // A resource whose value is a named code shows the name with the code. Any row may carry the
  // name, so the keys are read from all rows.
  const namedKeys = new Set(
    rows.flatMap(row =>
      resourcesOf(row)
        .filter(res => res.key && res.valueName)
        .map(res => res.key)
    )
  )

  // Collect dynamic resource keys from the first row that has resources
  const resourceColumns: CustomColumnGridConfig[] = []
  const seenKeys = new Set<string>()

  for (const row of rows) {
    if (Array.isArray(row.resources)) {
      for (const res of row.resources) {
        const headerName = res.name || res.key
        if (res.key && !seenKeys.has(res.key) && !fixedFields.has(res.key) && !fixedHeaders.has(headerName)) {
          seenKeys.add(res.key)
          resourceColumns.push({
            width: 150,
            minWidth: 120,
            flex: 1,
            field: res.key,
            headerName,
            filterSpec: { kind: resourceFilterKind(res.type, namedKeys.has(res.key)) },
            filterable: true,
            hideable: true,
            disableAggregation: true,
            ...(namedKeys.has(res.key) && { renderCell: renderCodeCell }),
          })
        }
      }
    }
    // Only need to scan until we've found all resource keys (first row usually has them all)
    if (seenKeys.size > 0) break
  }

  return [...FIXED_COLUMNS, ...resourceColumns]
}

export default FIXED_COLUMNS

/** A reading of the report as a grid row: each resource under its key, the names of its codes beside them. */
export function flattenSelectedMeterReading(
  item: ReportReading,
  id: number
): Record<string, unknown> {
  const { resources, ...rest } = item
  const row: Record<string, unknown> = { id, ...rest }
  if (Array.isArray(resources)) {
    for (const res of resources) {
      row[res.key] = res.value
    }
    const valueNames = collectValueNames(resources)
    if (valueNames) row.valueNames = valueNames
  }
  return row
}
